<template>
    <div class="route-page">
        <div class="container route-container">
            <!-- Date Navigation -->
            <div class="date-nav mb-3">
                <div class="d-flex align-items-center justify-content-between">
                    <button class="btn btn-outline-secondary date-nav-btn" @click="previousDay">
                        <i class="fa fa-chevron-left"></i>
                    </button>
                    <div class="text-center flex-grow-1 px-2 date-display" @click="toggleDatePicker">
                        <h5 class="mb-0 date-heading">{{ formattedDateShort }}</h5>
                        <small class="text-muted" v-if="isToday">Today</small>
                        <small class="text-primary" v-else>{{ daysFromToday }}</small>
                    </div>
                    <button class="btn btn-outline-secondary date-nav-btn" @click="nextDay">
                        <i class="fa fa-chevron-right"></i>
                    </button>
                </div>

                <!-- Date Picker -->
                <div v-if="showDatePicker" class="date-picker-wrap mt-2">
                    <input
                        type="date"
                        class="form-control"
                        v-model="pendingDate"
                    />
                    <div class="d-flex mt-2 date-picker-actions">
                        <button class="btn btn-sm btn-outline-secondary flex-fill" @click="setPendingToday">Today</button>
                        <button class="btn btn-sm btn-outline-secondary flex-fill mx-1" @click="setPendingTomorrow">Tomorrow</button>
                        <button class="btn btn-sm btn-primary flex-fill" @click="confirmDatePick">
                            <i class="fa fa-check mr-1"></i>Go
                        </button>
                    </div>
                </div>
            </div>

            <!-- Technician Selector (non-technicians only) -->
            <div v-if="isSuperAdmin || isAreaManager" class="mb-3">
                <label class="font-weight-bold mb-1">Select Technician</label>
                <multiselect
                    v-model="selectedTechnician"
                    :options="technicians"
                    :loading="loadingTechnicians"
                    label="name"
                    track-by="id"
                    placeholder="Search for a technician..."
                    :searchable="true"
                    :allow-empty="false"
                    @input="onTechnicianSelected"
                >
                    <template slot="singleLabel" slot-scope="{ option }">
                        {{ option.name }} <small class="text-muted">({{ option.area }})</small>
                    </template>
                    <template slot="option" slot-scope="{ option }">
                        <strong>{{ option.name }}</strong>
                        <small class="text-muted ml-1">– {{ option.area }}</small>
                    </template>
                </multiselect>
            </div>

            <!-- Loading State -->
            <loading-state v-if="loading" title="Loading route"></loading-state>

            <!-- Error State -->
            <div v-if="error && !loading" class="alert alert-danger">
                {{ error }}
            </div>

            <!-- Route Content -->
            <template v-if="!loading && !error && routeData">
                <!-- Route Stats -->
                <div class="row stats-row mb-3" v-if="routeData.data && routeData.data.attributes">
                    <div class="col-3 mb-2">
                        <div class="card card-body text-center stat-card p-2">
                            <small class="text-muted stat-label">Distance</small>
                            <strong class="stat-value">{{ routeData.data.attributes.distance_km }} km</strong>
                        </div>
                    </div>
                    <div class="col-3 mb-2">
                        <div class="card card-body text-center stat-card p-2">
                            <small class="text-muted stat-label">Travel</small>
                            <strong class="stat-value">{{ routeData.data.attributes.duration_hours }} hrs</strong>
                        </div>
                    </div>
                    <div class="col-3 mb-2">
                        <div class="card card-body text-center stat-card p-2">
                            <small class="text-muted stat-label">Service</small>
                            <strong class="stat-value">{{ routeData.data.attributes.service_hours }} hrs</strong>
                        </div>
                    </div>
                    <div class="col-3 mb-2">
                        <div class="card card-body text-center stat-card p-2">
                            <small class="text-muted stat-label">Capacity</small>
                            <strong class="stat-value">{{ routeData.data.attributes.capacity_used_percent }}%</strong>
                        </div>
                    </div>
                </div>

                <!-- Map -->
                <div class="mb-3 map-wrap" v-if="hasSteps">
                    <div ref="mapContainer" class="route-map"></div>
                </div>

                <!-- Job List -->
                <div v-if="hasSteps" class="stops-section">
                    <h5 class="mb-3">Stops ({{ jobSteps.length }} jobs)</h5>
                    <div class="card-table">
                        <div
                            v-for="(step, idx) in allSteps"
                            :key="`step-${idx}`"
                            class="card mb-2 step-card"
                            :class="stepCardClass(step)"
                        >
                            <div class="card-body step-body">
                                <div class="step-layout">
                                    <!-- Left column: type, UID, title -->
                                    <div class="step-left">
                                        <span class="badge" :class="stepBadgeClass(step)">
                                            {{ stepTypeLabel(step) }}
                                        </span>
                                        <div v-if="step.order_uid" class="step-uid">{{ step.order_uid }}</div>
                                        <div class="step-title" v-if="step.title">{{ step.title }}</div>
                                    </div>

                                    <!-- Right column: time, drive, customer info, devices -->
                                    <div class="step-right">
                                        <div class="step-info-row">
                                            <i class="fa fa-clock step-icon"></i>
                                            <span>{{ step.arrive_at }} - {{ step.leave_at }}</span>
                                        </div>
                                        <div v-if="step.service_minutes" class="step-info-row text-muted">
                                            <i class="fa fa-wrench step-icon"></i>
                                            <span>{{ step.service_minutes }} min service</span>
                                        </div>
                                        <div v-if="step.drive_time_minutes && ['job', 'end'].includes(step.type)" class="step-info-row text-muted">
                                            <i class="fa fa-car step-icon"></i>
                                            <span>{{ step.drive_time_minutes }} min &middot; {{ step.drive_distance_km }} km</span>
                                        </div>
                                        <template v-if="step.details">
                                            <div v-if="step.details.customer_name" class="step-info-row">
                                                <i class="fa fa-user step-icon"></i>
                                                <span>{{ step.details.customer_name }}</span>
                                            </div>
                                            <div v-if="step.details.postcode" class="step-info-row">
                                                <i class="fa fa-map-marker step-icon"></i>
                                                <span>{{ step.details.postcode }}</span>
                                            </div>
                                            <div v-if="step.details.customer_phone_number" class="step-info-row">
                                                <i class="fa fa-phone step-icon"></i>
                                                <a :href="`tel:${step.details.customer_phone_number}`">{{ step.details.customer_phone_number }}</a>
                                            </div>
                                        </template>

                                        <!-- Device Details -->
                                        <template v-if="step.devices && step.devices.length">
                                            <div class="step-devices-divider"></div>
                                            <div v-for="(device, dIdx) in step.devices" :key="`dev-${dIdx}`" class="step-device">
                                                <div v-if="device.make || device.model" class="step-info-row">
                                                    <i class="fa fa-mobile step-icon"></i>
                                                    <span>{{ [ucfirst(device.make), device.model].filter(Boolean).join(' ') }}</span>
                                                </div>
                                                <div v-if="device.board_size || device.colour" class="step-info-row text-muted">
                                                    <i class="fa fa-microchip step-icon"></i>
                                                    <span>{{ [device.board_size, device.colour].filter(Boolean).join(' · ') }}</span>
                                                </div>
                                                <div v-if="device.imei" class="step-info-row text-muted">
                                                    <i class="fa fa-barcode step-icon"></i>
                                                    <span>{{ device.imei }}</span>
                                                </div>
                                                <div v-if="device.so_number" class="step-info-row text-muted">
                                                    <i class="fa fa-file-text step-icon"></i>
                                                    <span>{{ device.so_number }}</span>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- No Route -->
                <empty-state
                    v-if="!hasSteps"
                    emptyIcon
                    icon="map"
                    title="No route calculated for this day"
                />
            </template>

            <!-- No Technician Selected (non-technician only) -->
            <empty-state
                v-if="(isSuperAdmin || isAreaManager) && !selectedTechnician && !loading"
                emptyIcon
                icon="map"
                title="Select a technician to view their route"
            />
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import polyline from '@mapbox/polyline';
import Multiselect from 'vue-multiselect';
import mapboxgl from 'mapbox-gl';
// eslint-disable-next-line import/no-webpack-loader-syntax
import MapboxWorker from 'worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker';

mapboxgl.workerClass = MapboxWorker;

export default {
    components: {
        Multiselect
    },

    data() {
        return {
            currentDate: this.moment().format('YYYY-MM-DD'),
            pendingDate: this.moment().format('YYYY-MM-DD'),
            loading: false,
            error: null,
            routeData: null,
            technicians: [],
            selectedTechnician: null,
            loadingTechnicians: false,
            showDatePicker: false,
            map: null,
        };
    },

    computed: {
        ...mapGetters([
            'user',
            'isTechnician',
            'isSuperAdmin',
            'isAreaManager'
        ]),

        formattedDate() {
            return this.moment(this.currentDate).format('dddd, D MMMM YYYY');
        },

        formattedDateShort() {
            return this.moment(this.currentDate).format('ddd, D MMM YYYY');
        },

        daysFromToday() {
            const diff = this.moment(this.currentDate).diff(this.moment().startOf('day'), 'days');
            if (diff === 1) return 'Tomorrow';
            if (diff === -1) return 'Yesterday';
            if (diff > 0) return `${diff} days ahead`;
            return `${Math.abs(diff)} days ago`;
        },

        isToday() {
            return this.currentDate === this.moment().format('YYYY-MM-DD');
        },

        technicianId() {
            if (this.isTechnician && this.user.technician) {
                return this.user.technician.id;
            }
            return this.selectedTechnician ? this.selectedTechnician.id : null;
        },

        hasSteps() {
            return this.routeData
                && this.routeData.included
                && this.routeData.included.steps
                && this.routeData.included.steps.job_count > 0;
        },

        allSteps() {
            if (!this.hasSteps) return [];
            return this.routeData.included.steps.items || [];
        },

        jobSteps() {
            return this.allSteps.filter(s => s.type === 'job');
        },

        routePath() {
            if (!this.routeData || !this.routeData.data || !this.routeData.data.attributes || !this.routeData.data.attributes.geometry) {
                return [];
            }
            try {
                const decoded = polyline.decode(this.routeData.data.attributes.geometry);
                return decoded.map(coord => [coord[1], coord[0]]); // Mapbox uses [lng, lat]
            } catch (e) {
                console.error('Failed to decode route polyline:', e);
                return [];
            }
        },

        mapMarkers() {
            if (!this.hasSteps) return [];

            return this.allSteps
                .filter(step => step.type !== 'break')
                .map((step) => {
                    let label = '';
                    let color = '#3200BE';

                    if (step.type === 'start') {
                        label = 'S';
                        color = '#0acb40';
                    } else if (step.type === 'end') {
                        label = 'E';
                        color = '#d52a04';
                    } else if (step.stop_number) {
                        label = String(step.stop_number);
                    }

                    return {
                        lngLat: [step.location[1], step.location[0]],
                        label,
                        color,
                        title: step.title || step.type,
                    };
                });
        }
    },

    mounted() {
        if (this.isSuperAdmin || this.isAreaManager) {
            this.loadTechnicians();
        } else if (this.isTechnician) {
            this.loadRoute();
        }
    },

    beforeDestroy() {
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    },

    methods: {
        toggleDatePicker() {
            this.showDatePicker = !this.showDatePicker;
            if (this.showDatePicker) {
                this.pendingDate = this.currentDate;
            }
        },

        setPendingToday() {
            this.pendingDate = this.moment().format('YYYY-MM-DD');
        },

        setPendingTomorrow() {
            this.pendingDate = this.moment().add(1, 'day').format('YYYY-MM-DD');
        },

        confirmDatePick() {
            if (this.pendingDate) {
                this.currentDate = this.pendingDate;
                this.showDatePicker = false;
                this.loadRoute();
            }
        },

        previousDay() {
            this.currentDate = this.moment(this.currentDate).subtract(1, 'day').format('YYYY-MM-DD');
            this.loadRoute();
        },

        nextDay() {
            this.currentDate = this.moment(this.currentDate).add(1, 'day').format('YYYY-MM-DD');
            this.loadRoute();
        },

        onTechnicianSelected(value) {
            if (value && value.id) {
                this.loadRoute();
            }
        },

        async loadTechnicians() {
            this.loadingTechnicians = true;
            try {
                const response = await this.$http.get('/api/v5/fleet/technicians-list');
                const raw = response.data || [];
                this.technicians = raw.map(tech => {
                    const item = tech.data || tech;
                    const attrs = item.attributes || item;
                    return {
                        id: item.id || tech.id,
                        name: attrs.name || 'Unknown',
                        area: attrs.area || '',
                    };
                });
            } catch (e) {
                console.error('Failed to load technicians:', e);
                this.technicians = [];
            } finally {
                this.loadingTechnicians = false;
            }
        },

        async loadRoute() {
            if (!this.technicianId) return;

            this.loading = true;
            this.error = null;
            this.routeData = null;

            // Remove existing map when loading new route
            if (this.map) {
                this.map.remove();
                this.map = null;
            }

            try {
                const response = await this.$http.get(
                    `/api/v5/fleet/technician/${this.technicianId}/route/${this.currentDate}`
                );
                this.routeData = response.data;
            } catch (e) {
                console.error('Failed to load route:', e);
                if (e.response && e.response.status === 404) {
                    this.error = 'No route found for this technician on this date.';
                } else {
                    this.error = 'Failed to load route. Please try again.';
                }
            } finally {
                this.loading = false;
                this.$nextTick(() => {
                    this.initMap();
                });
            }
        },

        initMap() {
            if (!this.$refs.mapContainer || !this.hasSteps) return;

            mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN;

            this.map = new mapboxgl.Map({
                container: this.$refs.mapContainer,
                style: 'mapbox://styles/mapbox/dark-v10',
                center: [this.mapMarkers[0].lngLat[0], this.mapMarkers[0].lngLat[1]],
                zoom: 10,
                attributionControl: false,
            });

            this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');

            this.map.on('load', () => {
                this.addRouteLayer();
                this.addMarkers();
                this.fitBounds();
            });
        },

        addRouteLayer() {
            if (!this.routePath.length || !this.map) return;

            this.map.addSource('route', {
                type: 'geojson',
                data: {
                    type: 'Feature',
                    geometry: {
                        type: 'LineString',
                        coordinates: this.routePath,
                    }
                }
            });

            this.map.addLayer({
                id: 'route-line',
                type: 'line',
                source: 'route',
                layout: {
                    'line-join': 'round',
                    'line-cap': 'round'
                },
                paint: {
                    'line-color': '#4285F4',
                    'line-width': 4,
                    'line-opacity': 0.9,
                }
            });
        },

        addMarkers() {
            if (!this.map) return;

            this.mapMarkers.forEach((marker) => {
                const el = document.createElement('div');
                el.className = 'mapbox-marker';
                el.style.backgroundColor = marker.color;
                el.style.width = '28px';
                el.style.height = '28px';
                el.style.borderRadius = '50%';
                el.style.border = '2px solid #fff';
                el.style.display = 'flex';
                el.style.alignItems = 'center';
                el.style.justifyContent = 'center';
                el.style.color = '#fff';
                el.style.fontWeight = 'bold';
                el.style.fontSize = '12px';
                el.style.boxShadow = '0 2px 4px rgba(0,0,0,0.3)';
                el.textContent = marker.label;

                new mapboxgl.Marker(el)
                    .setLngLat(marker.lngLat)
                    .addTo(this.map);
            });
        },

        fitBounds() {
            if (!this.map || !this.mapMarkers.length) return;

            const bounds = new mapboxgl.LngLatBounds();
            this.mapMarkers.forEach(marker => {
                bounds.extend(marker.lngLat);
            });

            this.map.fitBounds(bounds, {
                padding: { top: 40, right: 40, bottom: 40, left: 40 },
                maxZoom: 14,
            });
        },

        stepTypeLabel(step) {
            if (step.type === 'start') return 'Start';
            if (step.type === 'end') return 'End';
            if (step.type === 'break') return 'Break';
            if (step.is_revisit) return 'Revisit';
            return 'Standard';
        },

        stepBadgeClass(step) {
            if (step.type === 'start') return 'badge-success';
            if (step.type === 'end') return 'badge-danger';
            if (step.type === 'break') return 'badge-warning';
            if (step.is_revisit) return 'badge-info';
            return 'badge-primary';
        },

        ucfirst(str) {
            if (!str) return '';
            return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
        },

        stepCardClass(step) {
            if (step.type === 'break') return 'border-warning';
            if (step.type === 'start') return 'border-success';
            if (step.type === 'end') return 'border-danger';
            return '';
        }
    }
};
</script>

<style>
/* Mapbox GL CSS - must not be scoped */
@import '~mapbox-gl/dist/mapbox-gl.css';
</style>

<style scoped>
/* ── Layout ── */
.route-page {
    padding-bottom: env(safe-area-inset-bottom, 0);
}

.route-container {
    max-width: 720px;
    padding-top: 12px;
    padding-bottom: 24px;
}

/* ── Date Navigation ── */
.date-nav {
    background: #4A4F68;
    border-radius: 10px;
    padding: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    color: #fff;
}

.date-nav-btn {
    width: 42px;
    height: 42px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    padding: 0;
    flex-shrink: 0;
    color: #fff;
    border-color: rgba(255, 255, 255, 0.25);
}

.date-nav-btn:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.4);
}

.date-display {
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}

.date-heading {
    font-size: 1.05rem;
    color: #fff;
}

.date-picker-wrap {
    background: rgba(37, 42, 65, 0.5);
    border-radius: 8px;
    padding: 10px;
}

.date-picker-actions .btn {
    font-size: 0.82rem;
}

/* ── Stats ── */
.stats-row {
    margin-left: -4px;
    margin-right: -4px;
}

.stats-row > [class*="col-"] {
    padding-left: 4px;
    padding-right: 4px;
}

.stat-card {
    border-radius: 8px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-label {
    font-size: 0.7rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
}

.stat-value {
    font-size: 0.9rem;
}

/* ── Map ── */
.route-map {
    width: 100%;
    height: 45vh;
    min-height: 260px;
    border-radius: 12px;
    overflow: hidden;
}

/* ── Step cards ── */
.step-card {
    transition: all 0.2s ease;
    border-radius: 8px;
    overflow: hidden;
}

.step-card:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.step-body {
    padding: 12px 14px;
}

.step-layout {
    display: flex;
    gap: 12px;
}

.step-left {
    flex: 0 0 35%;
    max-width: 35%;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.step-left .badge {
    align-self: flex-start;
}

.step-uid {
    font-size: 0.78rem;
    font-weight: 700;
    color: #FE636C;
    word-break: break-all;
    line-height: 1.3;
}

.step-title {
    font-size: 0.85rem;
    font-weight: 600;
    word-break: break-word;
    line-height: 1.3;
    margin-top: 2px;
}

.step-right {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.84rem;
    border-left: 2px solid rgba(255, 255, 255, 0.08);
    padding-left: 12px;
}

.step-info-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-width: 0;
    line-height: 1.4;
}

.step-icon {
    width: 14px;
    text-align: center;
    flex-shrink: 0;
    margin-top: 2px;
    opacity: 0.6;
}

.step-info-row span,
.step-info-row a {
    min-width: 0;
    overflow-wrap: break-word;
    word-break: break-word;
}

/* ── Device details ── */
.step-devices-divider {
    height: 1px;
    background: rgba(255, 255, 255, 0.08);
    margin: 4px 0;
}

.step-device + .step-device {
    margin-top: 4px;
    padding-top: 4px;
    border-top: 1px dashed rgba(255, 255, 255, 0.06);
}

/* ── Mobile adjustments ── */
@media (max-width: 576px) {
    .route-container {
        padding-left: 10px;
        padding-right: 10px;
    }

    .date-heading {
        font-size: 0.95rem;
    }

    .stat-label {
        font-size: 0.62rem;
    }

    .stat-value {
        font-size: 0.8rem;
    }

    .stat-card {
        padding: 6px 4px !important;
    }

    .route-map {
        height: 38vh;
        min-height: 220px;
        border-radius: 8px;
    }

    .step-body {
        padding: 10px;
    }

    .step-layout {
        gap: 10px;
    }

    .step-title {
        font-size: 0.8rem;
    }

    .step-uid {
        font-size: 0.72rem;
    }

    .step-right {
        font-size: 0.78rem;
        padding-left: 10px;
        gap: 3px;
    }

    .stops-section h5 {
        font-size: 1rem;
    }
}
</style>



